import React from 'react';
import Layout from '../../components/Layout';
import Icon from '../../components/AppIcon';
import { Link } from 'react-router-dom';

const PartnershipsPage = () => {
  const partnerTypes = [
    {
      icon: 'Building2',
      title: 'Financial Institutions',
      description: 'Banks, credit unions, and fintech companies looking to serve the micro-business segment.',
      opportunities: ['White-label integration', 'Data sharing partnerships', 'Embedded finance products', 'Credit scoring collaboration'],
    },
    {
      icon: 'Landmark',
      title: 'Government Programs',
      description: 'Municipal, regional, and national programs focused on economic development.',
      opportunities: ['Formalization initiatives', 'Workforce development', 'Tax compliance programs', 'Small business grants'],
    },
    {
      icon: 'GraduationCap',
      title: 'Educational Institutions',
      description: 'Universities and training centers developing workforce skills.',
      opportunities: ['Internship programs', 'Curriculum development', 'Research partnerships', 'Certification programs'],
    },
    {
      icon: 'Plane',
      title: 'Travel & Tourism',
      description: 'Tourism boards and hospitality companies enhancing local experiences.',
      opportunities: ['Local service discovery', 'Tourism integration', 'Experience curation', 'Destination marketing'],
    },
    {
      icon: 'Rocket',
      title: 'Startup Accelerators',
      description: 'Accelerators and incubators supporting local entrepreneurs.',
      opportunities: ['Portfolio company access', 'Mentorship programs', 'Demo day partnerships', 'Co-investment opportunities'],
    },
    {
      icon: 'Building',
      title: 'Corporate Sponsors',
      description: 'Enterprises looking to support local communities and ESG initiatives.',
      opportunities: ['Impact sponsorship', 'Employee engagement', 'CSR programs', 'Brand partnerships'],
    },
  ];

  const integrationFramework = [
    { name: 'API Access', description: 'RESTful APIs for service discovery, booking, and payments' },
    { name: 'Data Sharing', description: 'Anonymized market insights and vendor analytics' },
    { name: 'White-label Solutions', description: 'Branded marketplace experiences for partners' },
    { name: 'Webhook Integrations', description: 'Real-time event notifications and triggers' },
  ];

  const programs = [
    {
      title: 'Workforce Inclusion Program',
      description: 'Partner with us to provide employment pathways for underserved communities.',
      icon: 'Users',
    },
    {
      title: 'Vendor Certification Program',
      description: 'Joint certification programs to elevate service quality standards.',
      icon: 'Award',
    },
    {
      title: 'Apprenticeship Model',
      description: 'Structured learning programs combining classroom and practical experience.',
      icon: 'BookOpen',
    },
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="py-20 bg-white">
        <div className="max-w-wide mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-sm font-medium text-accent uppercase tracking-wider mb-4">
                Partnerships
              </p>
              <h1 className="text-hero text-foreground mb-6">
                Build With Us
              </h1>
              <p className="text-body-large mb-8">
                Join our ecosystem as a strategic partner. Together, we can transform
                the service economy and create lasting impact across Latin America.
              </p>
              <a
                href="mailto:partnerships@unitedempresaas.com"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white font-medium rounded-brand-sm hover:bg-primary-light transition-colors"
              >
                Contact Partnerships Team
                <Icon name="ArrowRight" size={16} />
              </a>
            </div>

            <div className="bg-muted rounded-brand-lg p-8">
              <h3 className="text-lg font-semibold text-foreground mb-6">
                Partnership Benefits
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <Icon name="Check" size={20} className="text-accent mt-0.5" />
                  <span className="text-foreground">Access to 12,400+ verified service providers</span>
                </li>
                <li className="flex items-start gap-3">
                  <Icon name="Check" size={20} className="text-accent mt-0.5" />
                  <span className="text-foreground">Real-time market data and insights</span>
                </li>
                <li className="flex items-start gap-3">
                  <Icon name="Check" size={20} className="text-accent mt-0.5" />
                  <span className="text-foreground">API integration and white-label options</span>
                </li>
                <li className="flex items-start gap-3">
                  <Icon name="Check" size={20} className="text-accent mt-0.5" />
                  <span className="text-foreground">Co-marketing and brand exposure</span>
                </li>
                <li className="flex items-start gap-3">
                  <Icon name="Check" size={20} className="text-accent mt-0.5" />
                  <span className="text-foreground">Impact measurement and ESG reporting</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Partner Types */}
      <section className="py-20 bg-muted">
        <div className="max-w-wide mx-auto px-4 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-display text-foreground mb-6">
              Partnership Opportunities
            </h2>
            <p className="text-body-large">
              We work with a diverse range of partners to maximize our collective impact.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {partnerTypes.map((type, index) => (
              <div key={index} className="bg-white rounded-brand p-6 border border-border">
                <div className="w-12 h-12 bg-primary/10 rounded-brand-sm flex items-center justify-center mb-4">
                  <Icon name={type.icon} size={24} className="text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {type.title}
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  {type.description}
                </p>
                <ul className="space-y-2">
                  {type.opportunities.map((opp, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-foreground">
                      <Icon name="Check" size={14} className="text-accent" />
                      {opp}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Integration Framework */}
      <section className="py-20 bg-white">
        <div className="max-w-wide mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-display text-foreground mb-6">
                Integration Framework
              </h2>
              <p className="text-body-large mb-8">
                Our technical infrastructure is designed to seamlessly integrate with partner systems.
              </p>
              <div className="space-y-4">
                {integrationFramework.map((item, index) => (
                  <div key={index} className="flex items-start gap-4 p-4 bg-muted rounded-brand">
                    <Icon name="Code" size={20} className="text-primary mt-0.5" />
                    <div>
                      <div className="font-medium text-foreground">{item.name}</div>
                      <div className="text-sm text-muted-foreground">{item.description}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-primary rounded-brand-lg p-8 text-white">
              <h3 className="text-xl font-semibold mb-6">
                Technical Capabilities
              </h3>
              <ul className="space-y-4">
                <li className="flex items-center gap-3">
                  <Icon name="Shield" size={20} className="text-accent-light" />
                  <span>SOC 2 compliant infrastructure</span>
                </li>
                <li className="flex items-center gap-3">
                  <Icon name="Lock" size={20} className="text-accent-light" />
                  <span>End-to-end encryption</span>
                </li>
                <li className="flex items-center gap-3">
                  <Icon name="Zap" size={20} className="text-accent-light" />
                  <span>99.9% uptime SLA</span>
                </li>
                <li className="flex items-center gap-3">
                  <Icon name="Globe" size={20} className="text-accent-light" />
                  <span>Multi-region deployment</span>
                </li>
                <li className="flex items-center gap-3">
                  <Icon name="FileJson" size={20} className="text-accent-light" />
                  <span>Comprehensive API documentation</span>
                </li>
              </ul>
              <Link
                to="/technology"
                className="inline-flex items-center gap-2 mt-6 text-sm font-medium text-white hover:text-white/80 transition-colors"
              >
                View technical details
                <Icon name="ArrowRight" size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Programs */}
      <section className="py-20 bg-muted">
        <div className="max-w-wide mx-auto px-4 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-display text-foreground mb-6">
              Partner Programs
            </h2>
            <p className="text-body-large">
              Structured programs designed to create measurable impact together.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {programs.map((program, index) => (
              <div key={index} className="bg-white rounded-brand-lg p-8 border border-border text-center">
                <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Icon name={program.icon} size={32} className="text-accent" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-3">
                  {program.title}
                </h3>
                <p className="text-muted-foreground">
                  {program.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary">
        <div className="max-w-wide mx-auto px-4 lg:px-8 text-center">
          <h2 className="text-display text-white mb-6">
            Ready to Partner?
          </h2>
          <p className="text-white/70 mb-8 max-w-2xl mx-auto">
            Let's discuss how we can work together to transform the service economy
            and create lasting impact in Latin America.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="mailto:partnerships@unitedempresaas.com"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-primary font-medium rounded-brand-sm hover:bg-white/90 transition-colors"
            >
              <Icon name="Mail" size={16} />
              partnerships@unitedempresaas.com
            </a>
            <Link
              to="/investor-relations"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-white/30 text-white font-medium rounded-brand-sm hover:bg-white/10 transition-colors"
            >
              Investment Opportunities
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default PartnershipsPage;
